import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { LoginPage } from '@/auth/LoginPage';
import { AppShell } from '@/components/layout/AppShell';
import { EmptyState } from '@/components/ui/States';

/**
 * Routing.
 *
 * Route guards here are purely a navigation convenience — the backend
 * independently authenticates and authorises every `/admin/*` request, so a
 * user who manipulates the client cannot reach data this way.
 *
 * Pages are code-split: the admin has many screens and there is no reason to
 * ship the vehicle market view to someone opening the fuel page.
 */

const Overview = lazy(() => import('@/pages/Overview'));
const FuelPage = lazy(() => import('@/pages/fuel/FuelPage'));
const StationsPage = lazy(() => import('@/pages/fuel/StationsPage'));
const CurrencyPage = lazy(() => import('@/pages/currency/CurrencyPage'));
const DiscountsPage = lazy(() => import('@/pages/discounts/DiscountsPage'));
const ProductPage = lazy(() => import('@/pages/discounts/ProductPage'));
const TariffsPage = lazy(() => import('@/pages/tariffs/TariffsPage'));
const RealEstateMarket = lazy(() => import('@/pages/realEstate/MarketPage'));
const RealEstateListings = lazy(() => import('@/pages/realEstate/ListingsPage'));
const RealEstateDeals = lazy(() => import('@/pages/realEstate/DealsPage'));
const RealEstateDetail = lazy(() => import('@/pages/realEstate/DetailPage'));
const VehicleMarket = lazy(() => import('@/pages/vehicles/MarketPage'));
const VehicleListings = lazy(() => import('@/pages/vehicles/ListingsPage'));
const VehicleDeals = lazy(() => import('@/pages/vehicles/DealsPage'));
const VehicleDetail = lazy(() => import('@/pages/vehicles/DetailPage'));
const CandidatesPage = lazy(() => import('@/pages/posts/CandidatesPage'));
const PublishedPage = lazy(() => import('@/pages/posts/PublishedPage'));
const NewPostPage = lazy(() => import('@/pages/posts/NewPostPage'));
const PostDetailPage = lazy(() => import('@/pages/posts/PostDetailPage'));
const SchedulePage = lazy(() => import('@/pages/schedule/SchedulePage'));
const TelegramChannel = lazy(() => import('@/pages/telegram/ChannelPage'));
const TelegramPosts = lazy(() => import('@/pages/telegram/PostsPage'));
const TelegramAudience = lazy(() => import('@/pages/telegram/AudiencePage'));
const TelegramAnalytics = lazy(() => import('@/pages/telegram/AnalyticsPage'));
const SourcesPage = lazy(() => import('@/pages/system/SourcesPage'));
const ScrapeRunsPage = lazy(() => import('@/pages/system/ScrapeRunsPage'));
const JobsPage = lazy(() => import('@/pages/system/JobsPage'));
const ErrorsPage = lazy(() => import('@/pages/system/ErrorsPage'));
const ReportsPage = lazy(() => import('@/pages/system/ReportsPage'));
const AuditPage = lazy(() => import('@/pages/system/AuditPage'));
const ArticlesPage = lazy(() => import('@/pages/articles/ArticlesPage'));
const ArticleRunsPage = lazy(() => import('@/pages/articles/RunsPage'));
const ArticlePlaybookPage = lazy(() => import('@/pages/articles/PlaybookPage'));
const ArticleSectionsPage = lazy(() => import('@/pages/articles/SectionsPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const AdCampaignsPage = lazy(() => import('@/pages/ads/CampaignsPage'));
const AdCampaignPage = lazy(() => import('@/pages/ads/CampaignPage'));
const AdvertisersPage = lazy(() => import('@/pages/ads/AdvertisersPage'));
const AdInquiriesPage = lazy(() => import('@/pages/ads/InquiriesPage'));
const AdInvoicesPage = lazy(() => import('@/pages/ads/InvoicesPage'));

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-4 w-4 animate-spin text-[var(--color-text-subtle)]" />
    </div>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="Page not found"
      description="The page you requested does not exist in the admin."
    />
  );
}

export function App() {
  const { status } = useAuth();

  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') return <LoginPage />;

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<Overview />} />

          <Route path="/fuel" element={<FuelPage />} />
          <Route path="/fuel/stations" element={<StationsPage />} />

          <Route path="/currency" element={<CurrencyPage />} />

          <Route path="/discounts" element={<DiscountsPage />} />
          <Route path="/discounts/:productId" element={<ProductPage />} />

          <Route path="/tariffs" element={<TariffsPage />} />

          {/* Real Estate: Market | Listings | Deals as tabs within one section. */}
          <Route path="/real-estate" element={<RealEstateMarket />} />
          <Route path="/real-estate/listings" element={<RealEstateListings />} />
          <Route path="/real-estate/deals" element={<RealEstateDeals />} />
          <Route path="/real-estate/:listingId" element={<RealEstateDetail />} />

          <Route path="/vehicles" element={<VehicleMarket />} />
          <Route path="/vehicles/listings" element={<VehicleListings />} />
          <Route path="/vehicles/deals" element={<VehicleDeals />} />
          <Route path="/vehicles/:listingId" element={<VehicleDetail />} />

          <Route path="/posts" element={<CandidatesPage />} />
          <Route path="/posts/new" element={<NewPostPage />} />
          <Route path="/posts/published" element={<PublishedPage />} />
          <Route path="/posts/:postId" element={<PostDetailPage />} />

          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/runs" element={<ArticleRunsPage />} />
          <Route path="/articles/playbook" element={<ArticlePlaybookPage />} />
          <Route path="/articles/sections" element={<ArticleSectionsPage />} />

          <Route path="/schedule" element={<SchedulePage />} />

          <Route path="/telegram" element={<TelegramChannel />} />
          <Route path="/telegram/posts" element={<TelegramPosts />} />
          <Route path="/telegram/audience" element={<TelegramAudience />} />
          <Route path="/telegram/analytics" element={<TelegramAnalytics />} />

          <Route path="/system/sources" element={<SourcesPage />} />
          <Route path="/system/scrape-runs" element={<ScrapeRunsPage />} />
          <Route path="/system/jobs" element={<JobsPage />} />
          <Route path="/system/errors" element={<ErrorsPage />} />
          <Route path="/system/reports" element={<ReportsPage />} />
          <Route path="/system/audit" element={<AuditPage />} />

          <Route path="/analytics" element={<AnalyticsPage />} />

          <Route path="/ads" element={<AdCampaignsPage />} />
          <Route path="/ads/advertisers" element={<AdvertisersPage />} />
          <Route path="/ads/inquiries" element={<AdInquiriesPage />} />
          <Route path="/ads/invoices" element={<AdInvoicesPage />} />
          <Route path="/ads/:id" element={<AdCampaignPage />} />

          <Route path="/settings" element={<SettingsPage />} />

          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
